export type DiffChangeKind = "created" | "edited" | "deleted";
export type DiffLineKind = "context" | "added" | "removed";

export interface DiffSideView {
  readonly sha256: string;
  readonly revision: number;
  readonly size: number;
}

export interface DiffLineView {
  readonly kind: DiffLineKind;
  readonly text: string;
  readonly beforeLine: number | null;
  readonly afterLine: number | null;
  readonly missingFinalNewline?: boolean;
}

export interface DiffTextView {
  readonly kind: "text";
  readonly lines: readonly DiffLineView[];
  readonly added: number;
  readonly removed: number;
  readonly truncated: boolean;
}

export interface DiffBinaryView {
  readonly kind: "binary";
  readonly truncated: false;
}

export interface DiffFileView {
  readonly path: string;
  readonly kind: DiffChangeKind;
  readonly before: DiffSideView | null;
  readonly after: DiffSideView | null;
  readonly content: DiffTextView | DiffBinaryView;
}

export interface DiffTotals {
  readonly files: number;
  readonly added: number;
  readonly removed: number;
}

export function diffChangeLabel(kind: DiffChangeKind): string {
  switch (kind) {
    case "created":
      return "Created";
    case "deleted":
      return "Deleted";
    default:
      return "Edited";
  }
}

export function diffLineMarker(kind: DiffLineKind): string {
  switch (kind) {
    case "added":
      return "+";
    case "removed":
      return "-";
    default:
      return " ";
  }
}

export function diffLineNumberLabel(line: DiffLineView): string {
  const before = line.beforeLine === null ? "" : String(line.beforeLine);
  const after = line.afterLine === null ? "" : String(line.afterLine);
  return `${before}|${after}`;
}

export function diffLineAriaLabel(line: DiffLineView): string {
  const where = line.afterLine !== null ? `after line ${line.afterLine}` : line.beforeLine !== null ? `before line ${line.beforeLine}` : "unchanged";
  const described = line.kind === "added" ? "Added" : line.kind === "removed" ? "Removed" : "Context";
  const ending = line.missingFinalNewline ? ", file has no final newline" : "";
  return `${described} ${where}: ${line.text}${ending}`;
}

export function diffSideLabel(side: DiffSideView | null): string {
  return side === null ? "absent" : `revision ${side.revision} (${side.size} bytes, ${side.sha256.slice(0, 12)})`;
}

export function diffFileSummary(file: DiffFileView): string {
  const label = diffChangeLabel(file.kind);
  if (file.content.kind === "binary") return `${label} · binary`;
  const counts = file.content.lines.length === 0 ? "no line changes" : `+${file.content.added} −${file.content.removed}`;
  return `${label} · ${counts}`;
}

export function diffTransitionLabel(file: DiffFileView): string {
  if (file.kind === "created") return `new file at ${diffSideLabel(file.after)}`;
  if (file.kind === "deleted") return `removed from ${diffSideLabel(file.before)}`;
  return `${diffSideLabel(file.before)} → ${diffSideLabel(file.after)}`;
}

export function diffTotals(files: readonly DiffFileView[]): DiffTotals {
  return files.reduce<DiffTotals>(
    (totals, file) => ({
      files: totals.files + 1,
      added: totals.added + (file.content.kind === "text" ? file.content.added : 0),
      removed: totals.removed + (file.content.kind === "text" ? file.content.removed : 0),
    }),
    { files: 0, added: 0, removed: 0 },
  );
}
